import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import {
  DialogShellComponent,
  FilledButtonComponent,
  OutlinedButtonComponent,
  SelectFieldComponent,
} from '@custom-components';
import { PartnershipSummary } from '@core/models';
import { PartnershipService } from '@core/services';

export interface AddGroupToPartnershipDialogData {
  /** Partner that owns the group; only partnerships it owns are valid attachment targets. */
  ownerPartnerOid: string;
  /** OIDs of partnerships the group is already attached to; excluded from the picker. */
  existingPartnershipOids: string[];
}

@Component({
  selector: 'xemex-add-group-to-partnership-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatSelectModule,
    MatIconModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    SelectFieldComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './add-group-to-partnership-dialog.component.html',
})
export class AddGroupToPartnershipDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AddGroupToPartnershipDialogComponent>);
  private readonly partnershipService = inject(PartnershipService);
  readonly data = inject<AddGroupToPartnershipDialogData>(MAT_DIALOG_DATA);

  readonly partnershipControl = new FormControl<string | null>(null, Validators.required);
  /** Partnerships owned by the group's owner that the group isn't already attached to. */
  readonly partnerships = signal<PartnershipSummary[]>([]);
  readonly isLoading = signal(true);

  constructor() {
    // No role filter: the group's owner must own the target partnership, but a Xemex super-admin
    // managing another partner's group isn't the owner — so scope by ownerPartnerOid client-side,
    // which holds for both a partner owner and Xemex.
    this.partnershipService
      .getPartnerships({ limit: 100, offset: 0 })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: res => {
          const existing = new Set(this.data.existingPartnershipOids);
          this.partnerships.set(
            res.partnerships.filter(
              p =>
                p.ownerPartnerOid === this.data.ownerPartnerOid &&
                !existing.has(p.partnershipOid),
            ),
          );
          this.isLoading.set(false);
        },
        error: () => {
          this.partnerships.set([]);
          this.isLoading.set(false);
        },
      });
  }

  /** Label for a partnership option: the guest partner with the partnership type. */
  protected optionLabel(partnership: PartnershipSummary): string {
    return `${partnership.guestPartnerName} · ${partnership.partnershipType}`;
  }

  onAdd(): void {
    if (!this.partnershipControl.value) {
      this.partnershipControl.markAsTouched();
      return;
    }
    this.dialogRef.close(this.partnershipControl.value);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
